<script setup>
import { ref, shallowRef, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { MessagesSquare, Search, MapPin, X } from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { useMentionCandidates } from '@/composables/useMentionCandidates'
import ProjectTabs from '@/pages/Project/Components/ProjectTabs.vue'
import { groupReportsByDay, formatTimeFromNow } from '@/utils/datetime'
import ChatMessage from './ChatMessage.vue'
import ChatComposer from './ChatComposer.vue'
import ChatAvatar from './ChatAvatar.vue'

const route = useRoute()
const store = useStore()

const projectId = computed(() => route.params.project_id)
const tz = computed(() => store.getters['user/getUserSettings']?.tz)

const isEnabled = computed(() => store.getters['discussion/isEnabled'](projectId.value))
const messages = computed(() => store.getters['discussion/messages'](projectId.value))
const activeThreadId = computed(() => store.state.discussion.activeThreadId)
const pinDrop = computed(() => store.getters['discussion/pinDrop'])
const pendingAgent = computed(() => store.getters['discussion/pendingAgent'](projectId.value))
const draftLocation = computed(() => store.getters['discussion/draftLocation'])

// Grouped without cloning: a fresh object per message would change every
// ChatMessage's prop identity on each recompute and re-render the whole feed.
const groups = computed(() =>
  groupReportsByDay(messages.value, tz.value, (m) => m.createdAt)
)

const { mentionCandidates } = useMentionCandidates(projectId)

// --- search ---

const searchOpen = ref(false)
const searchQuery = ref('')
const searchInput = ref(null)
const isSearching = computed(() => !!searchQuery.value.trim())

const openSearch = async () => {
  searchOpen.value = true
  await nextTick()
  searchInput.value?.$el?.focus()
}

const closeSearch = () => {
  searchQuery.value = ''
  searchOpen.value = false
}

const searchResults = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return []
  return messages.value.filter((m) => {
    const haystack = [
      m.text,
      m.author?.display_name,
      ...(m.replies || []).flatMap((r) => [r.text, r.author?.display_name]),
      ...(m.attachments || []).map((a) => a.title || a.name || a.areaName || a.filename || ''),
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
})

const timeAgo = (ts) => formatTimeFromNow(new Date(ts).toISOString())

function goToResult(message) {
  searchQuery.value = ''
  nextTick(() => {
    flashMessage(message.id)
    if (message.location) {
      const map = store.getters['geo/getMap']
      map?.flyTo({ center: message.location, zoom: Math.max(map.getZoom(), 14), duration: 600 })
      store.commit('discussion/SET_ACTIVE_THREAD', message.id)
    }
  })
}

// --- feed scroll / highlight ---

const feedEl = ref(null)
// shallowRef + explicit delete: Vue calls a function ref with null when the
// element goes away, and without the delete every message ever rendered keeps
// its detached DOM subtree alive.
const itemEls = shallowRef({})
const flashId = ref(null)
let flashTimer = null

function setItemEl(id, el) {
  if (el) itemEls.value[id] = el
  else delete itemEls.value[id]
}

function scrollToBottom(behavior = 'auto') {
  const el = feedEl.value
  if (!el) return
  el.scrollTo({ top: el.scrollHeight, behavior })
}

function flashMessage(id) {
  itemEls.value[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  flashId.value = id
  clearTimeout(flashTimer)
  flashTimer = setTimeout(() => {
    flashId.value = null
  }, 1600)
}

// Opening a thread from a map marker highlights it in the feed too
watch(activeThreadId, (id) => {
  if (id && itemEls.value[id]) flashMessage(id)
})

// --- actions ---

function startPinDrop() {
  store.commit('discussion/SET_PIN_DROP', 'composer')
}

function locate(message) {
  const map = store.getters['geo/getMap']
  if (!map || !message.location) return
  map.flyTo({ center: message.location, zoom: Math.max(map.getZoom(), 13), duration: 600 })
  store.commit('discussion/SET_ACTIVE_THREAD', message.id)
}

// --- image drop ---

const composerEl = ref(null)
const dragging = ref(false)

function onDragOver(event) {
  if (Array.from(event.dataTransfer?.types || []).includes('Files')) dragging.value = true
}

function onDragLeave(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) dragging.value = false
}

function onDrop(event) {
  dragging.value = false
  const files = Array.from(event.dataTransfer?.files || [])
  if (files.length) composerEl.value?.addFiles(files)
}

// The server replies only when an enabled agent is explicitly @-mentioned.
// Mirror that for the typing indicator.
function noteAgentMention(mentions) {
  const mentioned = (mentions || []).find((m) => m.agent_id)
  if (mentioned?.display_name) {
    store.dispatch('discussion/notePendingAgent', {
      projectId: projectId.value,
      name: mentioned.display_name,
    })
  }
}

async function handleSend(payload) {
  const hadLocation = !!draftLocation.value
  const message = await store.dispatch('discussion/sendMessage', { projectId: projectId.value, ...payload })
  await nextTick()
  scrollToBottom('smooth')
  if (hadLocation) store.commit('discussion/SET_ACTIVE_THREAD', message.id)
  noteAgentMention(payload.mentions)
}

function handleReply(message, { text, mentions, attachments }) {
  store.dispatch('discussion/sendReply', {
    projectId: projectId.value,
    messageId: message.id,
    text,
    mentions,
    attachments,
  })
  noteAgentMention(mentions)
}

// New messages (own sends and live WS arrivals alike) keep the feed pinned to
// the bottom and the read watermark current while the tab is open.
const conversationUuid = computed(() => store.getters['chat/projectUuid'](projectId.value))
watch(
  () => messages.value.length,
  async () => {
    await nextTick()
    if (!flashId.value) scrollToBottom('smooth')
    if (conversationUuid.value) store.dispatch('chat/markRead', conversationUuid.value)
  }
)

// The typing indicator renders below the last message — bring it into view.
watch(pendingAgent, async (name) => {
  if (!name) return
  await nextTick()
  scrollToBottom('smooth')
})

onBeforeUnmount(() => {
  clearTimeout(flashTimer)
  itemEls.value = {}
})

onMounted(async () => {
  await store.dispatch('discussion/ensureLoaded', projectId.value)
  await nextTick()
  // A thread opened before navigating here (e.g. "Jump to chat" on a feature)
  // wins over the default bottom-pinned view.
  if (activeThreadId.value && itemEls.value[activeThreadId.value]) {
    flashMessage(activeThreadId.value)
  } else {
    scrollToBottom()
  }
  if (conversationUuid.value) store.dispatch('chat/markRead', conversationUuid.value)
})
</script>

<template>
  <ProjectTabs />

  <!-- Disabled state -->
  <div v-if="!isEnabled" class="px-3 py-6">
    <div class="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
      <MessagesSquare class="mx-auto mb-2 size-6" />
      Chat is disabled for this project.
      <router-link
        :to="{ name: 'ProjectChatSettings', params: { project_id: projectId } }"
        class="mt-1 block font-medium text-primary hover:underline"
      >
        Enable it in project settings
      </router-link>
    </div>
  </div>

  <template v-else>
    <!-- Search + members header -->
    <div class="flex items-center gap-2 border-b bg-background px-3 pb-2">
      <div v-if="searchOpen" class="relative min-w-0 flex-1 transition-all duration-200">
        <Search class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref="searchInput"
          v-model="searchQuery"
          placeholder="Search messages..."
          class="h-8 w-full pl-8 pr-8 text-sm"
          @keydown.escape="closeSearch"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          title="Clear search"
          aria-label="Clear search"
          @click="closeSearch"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
      <button
        v-else
        type="button"
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors hover:text-foreground"
        title="Search messages"
        aria-label="Search messages"
        @click="openSearch"
      >
        <Search class="h-4 w-4" />
      </button>
      <div v-if="!searchOpen" class="flex-1"></div>
    </div>

    <!-- Feed: its own scroller so the composer stays pinned at the bottom -->
    <div
      ref="feedEl"
      class="min-h-0 flex-1 overflow-y-auto px-3 py-2"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent.stop="onDrop"
    >
      <!-- Search results -->
      <template v-if="isSearching">
        <div
          v-if="!searchResults.length"
          class="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground"
        >
          No messages match “{{ searchQuery.trim() }}”
        </div>
        <div v-else class="space-y-2">
          <button
            v-for="message in searchResults"
            :key="message.id"
            class="flex w-full gap-2 rounded-lg border bg-card p-2.5 text-left transition-colors hover:border-primary/50"
            @click="goToResult(message)"
          >
            <ChatAvatar :author="message.author" size="sm" class="mt-0.5" />
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5 text-xs">
                <span class="font-semibold">{{ message.author.display_name }}</span>
                <span class="text-muted-foreground">{{ timeAgo(message.createdAt) }}</span>
                <MapPin v-if="message.location" class="size-3 self-center text-primary" />
              </div>
              <p class="truncate text-sm">{{ message.text }}</p>
              <p v-if="message.replies?.length" class="text-xs text-muted-foreground">
                {{ message.replies.length }} {{ message.replies.length === 1 ? 'reply' : 'replies' }}
              </p>
            </div>
          </button>
        </div>
      </template>

      <template v-else>
      <div
        v-if="!messages.length"
        class="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground"
      >
        <MessagesSquare class="mx-auto mb-2 size-6" />
        No messages yet — start the discussion below, or attach a location to pin
        a comment on the map
      </div>

      <section v-for="group in groups" :key="group.key" class="mb-2">
        <div class="relative my-3 text-center">
          <div class="absolute inset-x-0 top-1/2 border-t border-border/60"></div>
          <span class="relative z-10 bg-background px-2 text-xs font-medium text-muted-foreground">
            {{ group.label }}
          </span>
        </div>
        <div class="space-y-4">
          <div
            v-for="message in group.items"
            :key="message.uuid"
            :ref="(el) => setItemEl(message.id, el)"
            class="rounded-lg transition-shadow"
            :class="flashId === message.id ? 'ring-2 ring-primary/60' : ''"
          >
            <ChatMessage
              :message="message"
              :members="mentionCandidates"
              @reply="(p) => handleReply(message, p)"
              @locate="locate(message)"
            />
          </div>
        </div>
      </section>

      <div
        v-if="pendingAgent"
        class="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground"
      >
        <span class="flex gap-1">
          <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60"></span>
          <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:150ms]"></span>
          <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:300ms]"></span>
        </span>
        {{ pendingAgent }} is thinking…
      </div>
      </template>
    </div>

    <!-- Composer -->
    <div
      class="border-t bg-background px-3 py-2"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent.stop="onDrop"
    >
      <div
        v-if="dragging"
        class="mb-1.5 rounded-lg border border-dashed border-primary/60 bg-primary/5 px-2.5 py-1.5 text-center text-xs font-medium text-primary"
      >
        Drop images to attach
      </div>
      <div
        v-if="pinDrop === 'composer'"
        class="mb-1.5 rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary"
      >
        Click the map to place your comment — Esc to cancel
      </div>
      <ChatComposer
        ref="composerEl"
        :members="mentionCandidates"
        :draft-location="draftLocation"
        @send="handleSend"
        @pin-location="startPinDrop"
        @clear-location="store.commit('discussion/SET_DRAFT_LOCATION', null)"
      />
    </div>
  </template>
</template>
